/**
 * Mobile Gamepad Input Core Logic
 *
 * This module contains all the shared logic between MobileGamepadInput (legacy)
 * and MobileGamepadInputStandalone (new). By extracting this core logic, we ensure:
 *
 * 1. Single source of truth for input handling
 * 2. Changes to input logic automatically apply to both components
 * 3. Consistency between legacy and modern implementations
 * 4. Easier testing and maintenance
 *
 * DO NOT MODIFY without testing both MobileGamepadInput and MobileGamepadInputStandalone!
 */

import { XInputValues } from '@playcastdotio/CoreTypes';

/**
 * Animation step for thumbstick spring-back effect
 */
export const ANIMATION_STEP = 10000;

/**
 * Sensitivity multiplier for thumbstick movement
 */
export const THUMBSTICK_SENSITIVITY = 0.5;

/**
 * Maximum distance thumbstick can move from center
 */
export const STICK_MAX_DISTANCE = 25;

/**
 * Mapping of HTML element IDs to XInput button values
 */
export const GAMEPAD_BUTTON_MAP = {
  'right-ButtonSouth': XInputValues.A,
  'right-ButtonEast': XInputValues.B,
  'right-ButtonWest': XInputValues.X,
  'right-ButtonNorth': XInputValues.Y,
  'dpad-ButtonSouth': XInputValues.DPAD_DOWN,
  'dpad-ButtonEast': XInputValues.DPAD_RIGHT,
  'dpad-ButtonWest': XInputValues.DPAD_LEFT,
  'dpad-ButtonNorth': XInputValues.DPAD_UP,
  'left-ButtonMeta': XInputValues.BACK,
  'right-ButtonMeta': XInputValues.START,
  'left-ButtonBumper': XInputValues.LEFT_SHOULDER,
  'right-ButtonBumper': XInputValues.RIGHT_SHOULDER,
  'left-Trigger': XInputValues.LEFT_TRIGGER,
  'right-Trigger': XInputValues.RIGHT_TRIGGER,
  'right-StickClick': XInputValues.RIGHT_THUMB,
  'left-StickClick': XInputValues.LEFT_THUMB,
} as const;

/**
 * Calculate thumbstick X/Y values from touch position
 *
 * Converts screen coordinates to XInput thumbstick values (-32768 to 32767)
 * with sensitivity and distance clamping applied.
 *
 * @param start - Starting touch position
 * @param current - Current touch position
 * @param isPortrait - Whether the device is in portrait mode (rotates input 90° clockwise)
 */
export const calcThumbstickXY = (
  start: { x: number; y: number },
  current: { x: number; y: number },
  isPortrait = false
): { x: number; y: number } => {
  const circleRadius = 32767;

  let deltaX = (current.x - start.x) * THUMBSTICK_SENSITIVITY;
  let deltaY = (current.y - start.y) * THUMBSTICK_SENSITIVITY;

  // When in portrait mode, rotate input 90° clockwise to match visual rotation
  // For 90° clockwise rotation: new_x = old_y, new_y = -old_x
  if (isPortrait) {
    const temp = deltaX;
    deltaX = deltaY;
    deltaY = -temp;
  }

  const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

  const clampedDistance = Math.min(distance, STICK_MAX_DISTANCE);
  const scaleFactor = clampedDistance / distance;

  const scaledX = deltaX * scaleFactor;
  const scaledY = deltaY * scaleFactor * -1;

  const adjustedX = Math.round(circleRadius * (scaledX / STICK_MAX_DISTANCE));
  const adjustedY = Math.round(circleRadius * (scaledY / STICK_MAX_DISTANCE));

  return { x: adjustedX, y: adjustedY };
};

/**
 * Spring-back animation for returning thumbstick to neutral
 *
 * Returns a new value that's closer to zero by the given step amount.
 * Used for smooth thumbstick return animation.
 */
export const springBack = (currentValue: number, step: number): number => {
  if (Math.abs(currentValue) < step) {
    return 0;
  }
  return currentValue - Math.sign(currentValue) * step;
};

/**
 * Check if a touch point is within a button's bounds
 */
export const isInButton = (
  rect: DOMRect | undefined,
  touchX: number,
  touchY: number
): boolean => {
  return rect !== undefined &&
    touchX >= rect.left &&
    touchX <= rect.right &&
    touchY >= rect.top &&
    touchY <= rect.bottom;
};

/**
 * Get D-pad button element rectangles
 */
export const getDPadRects = () => {
  return {
    north: document.getElementById('dpad-ButtonNorth')?.getBoundingClientRect(),
    south: document.getElementById('dpad-ButtonSouth')?.getBoundingClientRect(),
    east: document.getElementById('dpad-ButtonEast')?.getBoundingClientRect(),
    west: document.getElementById('dpad-ButtonWest')?.getBoundingClientRect(),
  };
};

/**
 * Detect which D-pad buttons are pressed based on touch position
 *
 * Supports diagonal inputs (e.g., pressing both up+left simultaneously)
 */
export const detectDPadButtons = (touchX: number, touchY: number): number => {
  const rects = getDPadRects();

  const inNorth = isInButton(rects.north, touchX, touchY);
  const inSouth = isInButton(rects.south, touchX, touchY);
  const inEast = isInButton(rects.east, touchX, touchY);
  const inWest = isInButton(rects.west, touchX, touchY);

  let buttons = 0;

  // Diagonal detection
  if (inNorth && inWest) {
    buttons |= GAMEPAD_BUTTON_MAP['dpad-ButtonNorth'];
    buttons |= GAMEPAD_BUTTON_MAP['dpad-ButtonWest'];
  } else if (inNorth && inEast) {
    buttons |= GAMEPAD_BUTTON_MAP['dpad-ButtonNorth'];
    buttons |= GAMEPAD_BUTTON_MAP['dpad-ButtonEast'];
  } else if (inSouth && inWest) {
    buttons |= GAMEPAD_BUTTON_MAP['dpad-ButtonSouth'];
    buttons |= GAMEPAD_BUTTON_MAP['dpad-ButtonWest'];
  } else if (inSouth && inEast) {
    buttons |= GAMEPAD_BUTTON_MAP['dpad-ButtonSouth'];
    buttons |= GAMEPAD_BUTTON_MAP['dpad-ButtonEast'];
  } else {
    // Single direction
    if (inNorth) buttons |= GAMEPAD_BUTTON_MAP['dpad-ButtonNorth'];
    if (inSouth) buttons |= GAMEPAD_BUTTON_MAP['dpad-ButtonSouth'];
    if (inEast) buttons |= GAMEPAD_BUTTON_MAP['dpad-ButtonEast'];
    if (inWest) buttons |= GAMEPAD_BUTTON_MAP['dpad-ButtonWest'];
  }

  return buttons;
};

/**
 * Clear all D-pad button flags
 */
export const CLEAR_DPAD_BUTTONS =
  GAMEPAD_BUTTON_MAP['dpad-ButtonNorth'] |
  GAMEPAD_BUTTON_MAP['dpad-ButtonSouth'] |
  GAMEPAD_BUTTON_MAP['dpad-ButtonEast'] |
  GAMEPAD_BUTTON_MAP['dpad-ButtonWest'];

